// 📁 Imports

import axios from "axios";
import { addRecaptchaTokenToForm } from "../../../MerlinCMS-core/src/js/components/forms/recaptcha-v3.js";
import {
  buttonSpinnerAdd,
  buttonSpinnerRemove,
} from "../../../MerlinCMS-core/src/js/utils/button-spinner.js";
import {
  modalShow,
  modalUpdateMessage,
} from "../../../MerlinCMS-core/src/js/components/modal.js";
import { alertShow } from "../../../MerlinCMS-core/src/js/components/alert.js";
import { formValidator } from "../../../MerlinCMS-core/src/js/utils/form-validator.js";
import { clearErrorMessage } from "../../../MerlinCMS-core/src/js/utils/input-message.js";
import { uiManager } from "../managers/UIManager.js";

// 💾 Variables
const forms = document.querySelectorAll("[data-form]");

// 📐 Functions
const formSend = async (form) => {
  const formAction = form.dataset.formPost;
  const alertSuccess = form.querySelector("[data-alert=success]");
  const alertError = form.querySelector("[data-alert=error]");
  const modal = form.querySelector("[data-modal]");
  const formSubmitButton = form.querySelector('button[type="submit"]');

  // Get messageType from form dataset, default to 'toast-default'
  const messageType = form.dataset.messageType || "toast-default";

  if (formSubmitButton) {
    buttonSpinnerAdd(formSubmitButton);
  }

  try {
    // Zpracování reCAPTCHA pokud je vyžadována
    const recaptchaAction = form.dataset.recaptcha;
    if (recaptchaAction) {
      try {
        await addRecaptchaTokenToForm(form, recaptchaAction);
      } catch (error) {

        // Zobrazíme chybu uživateli
        const errorMessage =
          "Došlo k chybě při ověřování. Zkuste to prosím znovu.";
        if (modal) {
          modalUpdateMessage(modal, [{ type: "error", message: errorMessage }]);
          modalShow(modal);
        }

        if (formSubmitButton) {
          buttonSpinnerRemove(formSubmitButton);
        }
        return;
      }
    }

    const formData = serializeFormToJSON(form);


    const response = await axios.post(
      `${formAction}`,
      JSON.stringify(formData),
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    window.dispatchEvent(
      new CustomEvent("onSubmitForm", {
        detail: {
          message: response.data.messages,
        },
      }),
    );

    // Handle success message based on messageType
    if (messageType === "modal" && modal) {
      modalUpdateMessage(modal, response.data.messages);
      modalShow(modal);
      formReset(form);
    } else if (
      (messageType === "toast-default" || messageType === "toast-inline") &&
      alertSuccess
    ) {
      alertShow(alertSuccess, response.data.messages);
      formReset(form);
    }

    // Check if form is inside contact modal and close it on success
    const contactModal = form.closest('.contact-modal');
    if (contactModal) {
      
      // Close modal immediately
      uiManager.hideContactModal();
      
      // Show global success alert
      const globalSuccessAlert = document.querySelector('#global-form-alert-success');
      if (globalSuccessAlert) {
        alertShow(globalSuccessAlert, response.data.messages);
      }
    }

    if (formSubmitButton) {
      buttonSpinnerRemove(formSubmitButton);
    }

    return response;
  } catch (error) {
    // Check if form is inside contact modal and show global error alert
    const contactModal = form.closest('.contact-modal');
    if (contactModal) {
      
      // Show global error alert
      const globalErrorAlert = document.querySelector('#global-form-alert-error');
      if (globalErrorAlert) {
        alertShow(
          globalErrorAlert,
          error.response?.data?.messages || "Formulár sa nepodarilo odoslat"
        );
      }
    } else {
      // Handle error message based on messageType for non-modal forms
      if (messageType === "modal" && modal) {
        modalUpdateMessage(
          modal,
          error.response?.data?.messages || {
            error: ["Formulár sa nepodarilo odoslat"],
          },
        );
        modalShow(modal);
      } else if (
        (messageType === "toast-default" || messageType === "toast-inline") &&
        alertError
      ) {
        alertShow(
          alertError,
          error.response?.data?.messages || "Formulár sa nepodarilo odoslat",
        );
      }
    }

    if (formSubmitButton) {
      buttonSpinnerRemove(formSubmitButton);
    }
  }
};

// Form reset (reset values for all inputs)
const formReset = (form) => {
  const formInputs = form.querySelectorAll(
    "input:not([type='hidden']):not(.antispamanswer), textarea",
  );

  formInputs.forEach((input) => {
    clearErrorMessage(input);
    if (
      input.type === "text" ||
      input.type === "email" ||
      input.type === "tel" ||
      input.type === "number" ||
      input.tagName.toLowerCase() === "textarea"
    ) {
      input.value = "";
    }

    if (input.type === "checkbox" || input.type === "radio") {
      input.checked = false;
    }
  });
};

const serializeFormToJSON = (form) => {
  const inputs = form.querySelectorAll("input, select, textarea");
  const data = {};

  inputs.forEach((input) => {
    const { name, type } = input;

    if (!name || input.disabled) return;

    switch (type) {
      case "checkbox":
        if (input.checked) {
          if (!data[name]) data[name] = [];
          data[name].push(input.value);
        }
        break;

      case "radio":
        if (input.checked) {
          data[name] = input.value;
        }
        break;

      default:
        data[name] = input.value;
    }
  });

  for (const key in data) {
    if (Array.isArray(data[key]) && data[key].length === 1) {
      data[key] = data[key][0];
    } else if (
      typeof data[key] === "string" &&
      data[key].startsWith("[") &&
      data[key].endsWith("]")
    ) {
      data[key] = JSON.parse(data[key]);
    }
  }

  return data;
};

// 🖱️ Event listener
if (forms.length !== 0) {
  Array.from(forms).forEach((form) => {
    form.addEventListener("submit", (e) => {
      e.preventDefault(); // Always prevent default form submission

      const formSubmitButton = form.querySelector('button[type="submit"]');

      // Prevent form submission if button is disabled
      if (
        formSubmitButton &&
        (formSubmitButton.disabled ||
          formSubmitButton.classList.contains("button--disabled"))
      ) {
        return;
      }

      const formIdentificator = form.dataset.form;
      const validationResult = formValidator(formIdentificator);
      const formIsValid = validationResult.isValid;

      if (formIsValid) {
        formSend(form);
      } else {
        // TEMPORARILY DISABLED: Show error alert with specific errors
        // const alertError = form.querySelector("[data-alert=error]");
        // const messageType = form.dataset.messageType || 'toast-default';

        // if (alertError) {
        //   // Create error message from validation results with translations
        //   let errorMessage = "Prosím opravte následující chyby:";
        //   if (validationResult.invalidInputs && validationResult.invalidInputs.length > 0) {
        //     const errorList = validationResult.invalidInputs.map(item => {
        //       const fieldName = item.input || 'Neznámé pole';
        //       const errorMessages = item.errors.map(error => {
        //         // Handle both old string format and new object format
        //         if (typeof error === 'object' && error.message) {
        //           return error.message;
        //         } else if (typeof error === 'object' && error.type) {
        //           return getErrorTranslation(error.type);
        //         } else if (typeof error === 'string') {
        //           // Try to get translation for string error type
        //           return getErrorTranslation(error) || error;
        //         } else {
        //           return 'Neznámá chyba';
        //         }
        //       }).join(', ');
        //       return `${errorMessages}`;
        //     }).join("<br>");
        //     errorMessage += "<br>" + errorList;
        //   } else {
        //     errorMessage = "Prosím vyplňte všechna povinná pole správně.";
        //   }

        //   alertShow(alertError, errorMessage);
        // }

        // Focus on first invalid field
        const firstInvalidInput = form.querySelector(
          "[data-validate].input--error",
        );
        if (firstInvalidInput) {
          firstInvalidInput.focus();
        }

        return; // Explicit return to prevent any further execution
      }
    });
  });
}

// 📤 Export
export { formSend, formReset };
